import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Image, Smartphone, Upload } from 'lucide-react';
import { toast } from 'sonner';

export function PwaTab() {
  const [appIcon, setAppIcon] = useState<File | null>(null);
  const [iconPreview, setIconPreview] = useState('');

  useEffect(() => {
    return () => { if (iconPreview) URL.revokeObjectURL(iconPreview); };
  }, [iconPreview]);

  const handleIconUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image must be less than 2MB'); return; }
    if (iconPreview) URL.revokeObjectURL(iconPreview);
    setAppIcon(file);
    setIconPreview(URL.createObjectURL(file));
    toast.success('Icon updated locally. Redeploy to publish.');
  };

  return (
    <Card className="overflow-hidden rounded-3xl border border-border bg-card shadow-glass">
      <CardHeader className="border-b bg-muted/30 p-8">
        <CardTitle className="flex items-center gap-3 text-foreground font-black uppercase tracking-tight"><Smartphone className="h-6 w-6" />PWA Application Icon</CardTitle>
        <CardDescription className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Preview is local; persistence needs backend pipeline support.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 p-6 sm:p-10">
        <div className="flex flex-col items-start gap-8 lg:flex-row">
          <div className="w-full flex-1 space-y-6">
            <div className="relative cursor-pointer rounded-3xl border-2 border-dashed border-border p-8 text-center transition-all hover:bg-muted/50">
              <input type="file" accept="image/*" onChange={handleIconUpload} className="absolute inset-0 h-full w-full cursor-pointer opacity-0" />
              <div className="space-y-4">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-muted"><Upload className="h-8 w-8 text-muted-foreground" /></div>
                <div>
                  <p className="font-black uppercase text-foreground">Drop icon here or click to upload</p>
                  <p className="mt-1 text-xs font-bold uppercase tracking-widest text-muted-foreground">PNG, SVG, or WebP. 192×192 or 512×512 recommended.</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-muted p-4 text-center">
                <Image className="mx-auto mb-2 h-6 w-6 text-muted-foreground" /><p className="text-xs font-black uppercase text-muted-foreground">192×192</p><p className="text-[10px] text-muted-foreground/70">Small devices</p>
              </div>
              <div className="rounded-2xl bg-muted p-4 text-center">
                <Image className="mx-auto mb-2 h-6 w-6 text-muted-foreground" /><p className="text-xs font-black uppercase text-muted-foreground">512×512</p><p className="text-[10px] text-muted-foreground/70">Large devices + splash</p>
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center gap-4">
            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">Preview</p>
            <div className="flex h-32 w-32 items-center justify-center overflow-hidden rounded-3xl bg-muted shadow-xl">
              {iconPreview ? <img src={iconPreview} alt="App icon preview" className="h-full w-full object-cover" /> : <div className="p-4 text-center text-muted-foreground"><p className="text-xs font-black">Current</p><p className="text-[10px] opacity-70">No custom icon</p></div>}
            </div>
            {appIcon && (
              <Button variant="ghost" onClick={() => { if (iconPreview) URL.revokeObjectURL(iconPreview); setAppIcon(null); setIconPreview(''); }} className="text-xs font-black uppercase text-destructive">Remove</Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
